import { create } from 'zustand'
import { fetchAuthSession, signOut } from 'aws-amplify/auth'
import { getUserProfile, getUserUsage } from '@/api/users'
import type { UserProfile, Usage, PlanInfo } from '@/types/api'
import { tierAllows, type Feature, type Tier } from '@/lib/tiers'
import { identifyUser, resetUser } from '@/lib/analytics'
import { setErrorUser, captureError } from '@/lib/errorReporting'

interface CognitoTokens {
  idToken: string
  accessToken: string
  refreshToken: string
  expiresAt: number
}

export interface AuthState {
  user: UserProfile | null
  usage: Usage | null
  tokens: CognitoTokens | null
  redeemedCode: string | null
  redeemedTier: string | null
  isAuthenticated: boolean
  isInitialized: boolean

  login: (tokens: CognitoTokens, user: UserProfile) => void
  logout: () => void
  setRedeemed: (code: string, tier: string) => void
  clearRedeemed: () => void
  /** Set the signed-in user's tier in place (e.g. after in-account code
   * redemption). No-op when unauthenticated. Callers should also fetchUsage(). */
  setUserTier: (tier: Tier) => void
  refreshSession: () => Promise<void>
  initAuth: () => Promise<void>
  /** Fetch the current user's periodized usage into the store. */
  fetchUsage: () => Promise<void>
}

let authInitPromise: Promise<void> | null = null

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  usage: null,
  tokens: null,
  redeemedCode: null,
  redeemedTier: null,
  isAuthenticated: false,
  isInitialized: false,

  login: (tokens, user) => {
    set({ tokens, user, isAuthenticated: true })
    // O3: identify by the opaque Cognito sub on PostHog + Sentry (no PII).
    identifyUser(user.user_id, { tier: user.tier })
    setErrorUser(user.user_id)
  },

  logout: () => {
    signOut().catch(() => {})
    // O3: clear analytics + error-reporting identity on logout.
    resetUser()
    setErrorUser(null)
    set({
      tokens: null,
      user: null,
      usage: null,
      isAuthenticated: false,
      redeemedCode: null,
      redeemedTier: null,
    })
  },

  setRedeemed: (code, tier) => set({ redeemedCode: code, redeemedTier: tier }),
  clearRedeemed: () => set({ redeemedCode: null, redeemedTier: null }),

  setUserTier: (tier) => {
    const { user } = get()
    if (!user) return
    const updated = { ...user, tier }
    set({ user: updated })
    // Keep analytics identity's tier property in sync (matches login/initAuth).
    identifyUser(updated.user_id, { tier: updated.tier })
  },

  /** Fetch the current user's periodized usage into the store. Best-effort:
   * a failure leaves `usage` as-is so the UI degrades gracefully (C4 renders
   * nothing / "—" when usage is null). Called from initAuth, and re-called by
   * C4 after a successful generation and after preview calls to keep counts
   * live. No-op when unauthenticated. */
  fetchUsage: async () => {
    const { user } = get()
    if (!user) return
    try {
      const usage = await getUserUsage(user.user_id)
      set({ usage })
    } catch (err) {
      // Leave existing usage untouched so the UI degrades gracefully, but do
      // NOT swallow silently: a schema-validation failure here (e.g. a tier the
      // frontend's TierSchema doesn't know about) leaves the badge stuck on "—"
      // with no signal. Report it so the next drift is visible.
      captureError(err, 'fetchUsage')
    }
  },

  refreshSession: async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true })
      const tokens = session.tokens
      if (!tokens?.idToken || !tokens?.accessToken) {
        get().logout()
        return
      }
      set({
        tokens: {
          idToken: tokens.idToken.toString(),
          accessToken: tokens.accessToken.toString(),
          refreshToken: '',
          expiresAt: (tokens.idToken.payload.exp as number) * 1000,
        },
      })
    } catch {
      get().logout()
    }
  },

  initAuth: () => {
    if (authInitPromise) return authInitPromise
    authInitPromise = (async () => {
      try {
        const session = await fetchAuthSession()
        const tokens = session.tokens

        if (!tokens?.idToken || !tokens?.accessToken) {
          set({ isInitialized: true })
          return
        }

        const userId = tokens.idToken.payload.sub as string
        const cognitoTokens: CognitoTokens = {
          idToken: tokens.idToken.toString(),
          accessToken: tokens.accessToken.toString(),
          refreshToken: '',
          expiresAt: (tokens.idToken.payload.exp as number) * 1000,
        }

        // Set tokens first so the API client can authenticate the profile fetch
        set({ tokens: cognitoTokens })

        let profile: UserProfile | null = null
        try {
          profile = await getUserProfile(userId)
        } catch {
          // Profile fetch failed — use minimal fallback
        }

        const resolvedUser = profile ?? {
          user_id: userId,
          email: (tokens.idToken.payload.email as string) ?? '',
          tier: 'starter',
        }
        set({
          tokens: cognitoTokens,
          user: resolvedUser,
          isAuthenticated: true,
          isInitialized: true,
        })

        // O3: identify from the single auth surface so every entry path (login,
        // redeem, page reload via initAuth) identifies once with the same
        // opaque Cognito sub the backend logs. No PII (sub + tier only).
        identifyUser(resolvedUser.user_id, { tier: resolvedUser.tier })
        setErrorUser(resolvedUser.user_id)

        // Best-effort usage hydration (does not block init).
        void get().fetchUsage()
      } catch {
        set({ isInitialized: true })
      }
    })()
    return authInitPromise
  },
}))

// ─── Usage selectors / helpers ──────────────────────────────────────────────────
// Components read these instead of re-deriving from `usage` so the "unlimited"
// (null) handling lives in one place. Usage at call sites:
//   const remaining = useAuthStore(selectGenerationsRemaining)
//   const window = useAuthStore(selectGenerationsWindow)
//   const plan = useAuthStore(selectPlan)
//   const canSatellite = useAuthStore(selectTierAllows('satellite'))

/** Generations remaining this year.
 * Three-state contract (matches `formatGenerationsLeft`):
 *   - a number  → metered remaining (consumers render "N left"; a concrete 0 ⇒ at limit)
 *   - `null`    → UNLIMITED (render "Unlimited", never "0 left") — the ONLY unlimited
 *                 sentinel (the wire never sends -1)
 *   - `undefined` → usage not loaded yet (render "—")
 * Loading MUST stay distinct from unlimited: collapsing both to `null` made a
 * metered (e.g. starter) user flash "Unlimited" before usage hydrated. */
export const selectGenerationsRemaining = (s: AuthState): number | null | undefined =>
  s.usage ? s.usage.generations.remaining : undefined

/** The usage payload's plan block, or null before usage hydrates (C1). */
export const selectPlan = (s: AuthState): PlanInfo | null => s.usage?.plan ?? null

/** Which window generations are counted in (C1). `undefined` before usage
 * hydrates — callers default the DISPLAY to 'year' (every pre-Stripe user is
 * year-window, so the fallback can never show wrong copy to a real user
 * mid-hydrate). */
export const selectGenerationsWindow = (s: AuthState): 'year' | 'cycle' | undefined =>
  s.usage?.generations.window

/** Live previews remaining today, or null when usage isn't loaded.
 * = previews.daily_cap - previews.used_day (clamped ≥ 0). */
export const selectPreviewsRemaining = (s: AuthState): number | null =>
  s.usage ? Math.max(0, s.usage.previews.daily_cap - s.usage.previews.used_day) : null

/** Whether the signed-in user's tier allows a gated feature. Prefers the usage
 * payload's `features` flags when present (authoritative for the live user),
 * else falls back to the static tier mirror (A1's `tierAllows`). Feature keys
 * are exactly `'satellite'` / `'custom_base'` / `'download'` /
 * `'commercial_license'` (C-TIERS). */
export const selectTierAllows =
  (feature: Feature) =>
  (s: AuthState): boolean => {
    if (s.usage) return s.usage.features[feature]
    return s.user ? tierAllows(s.user.tier, feature) : false
  }

/** Public CTA display only: session tokens are available before profile hydration.
 * null means the initial session check is pending. Protected routes still use
 * isInitialized + isAuthenticated so they wait for the user's profile. */
export const selectPublicAuthStatus = (s: AuthState): boolean | null =>
  s.isAuthenticated || s.tokens !== null ? true : s.isInitialized ? false : null
